import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Button } from '@ai-tutor/ui';
import type {
  HighlightCarousel,
  LessonHighlightsResponse,
} from '@/lib/api-client';
import { cn } from '@/lib/utils';
import {
  TemplateCard,
  pickTemplateForConcept,
} from '@/components/features/highlights-template';
import { InlineMarkdown } from '@/components/core/markdown/markdown';
import { HighlightsEmptyState } from './HighlightsEmptyState';

const SWIPE_THRESHOLD = 50; // px

/** Fixed square design canvas each card lays out against; scaled to fit. */
const CARD_DESIGN_PX = 800;

/**
 * A card arrow BESIDE the card, not over it: laid over the card's edge it
 * covered the start and end of every title line. Quiet until the reader is
 * hovered; hidden on small screens, where swiping is natural and the width
 * is better spent on the card.
 */
function CardArrow({
  direction,
  onClick,
}: {
  direction: 'prev' | 'next';
  onClick: () => void;
}) {
  const Icon = direction === 'prev' ? ChevronLeft : ChevronRight;
  return (
    <button
      type='button'
      onClick={onClick}
      aria-label={direction === 'prev' ? 'Previous card' : 'Next card'}
      className='z-20 hidden h-10 w-10 shrink-0 items-center justify-center rounded-full bg-background/80 text-foreground/70 opacity-0 shadow-sm backdrop-blur transition-opacity hover:bg-background hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 group-hover/reader:opacity-100 dark:focus-visible:ring-primary/40 md:inline-flex'
    >
      <Icon className='h-5 w-5' />
    </button>
  );
}

export interface HighlightsReaderProps {
  carousels: LessonHighlightsResponse['carousels'];
  /** Concept to open on. */
  initialConceptIdx?: number;
  /** Close the reader and return to the overview grid. */
  onClose?: () => void;
  /** The concept now shown (its id) - for study time per concept. */
  onConceptChange?: (conceptId: string | null) => void;
}

/**
 * The full-viewport Inshorts/Stories-style reader - two navigation axes, both
 * wrapping:
 *
 *  - Horizontal (within a concept): ←/→ keys, horizontal swipe, or the
 *    floating chevrons.
 *  - Vertical (between concepts): ↑/↓ keys or vertical swipe. Resets the card
 *    index to 0 when the concept changes.
 *
 * Escape closes. Swipes are classified by the larger delta and ignored below a
 * 50px threshold.
 *
 * Extracted from `LessonHighlightsCarousel` so the navigation can be storied
 * against fixture carousels without going through `StepGate` and the artifact
 * poll - it takes `carousels` as a prop and owns nothing but its two indices.
 */
export function HighlightsReader({
  carousels,
  initialConceptIdx = 0,
  onClose,
  onConceptChange,
}: HighlightsReaderProps) {
  const [conceptIdx, setConceptIdx] = useState(initialConceptIdx);
  const shownConceptId = carousels[conceptIdx]?.concept_id ?? null;
  useEffect(() => {
    onConceptChange?.(shownConceptId);
  }, [shownConceptId, onConceptChange]);
  const [cardIdx, setCardIdx] = useState(0);

  // Size the card as a true square = the largest that fits the available area.
  // Pure CSS aspect-square + w-full/max-h-full yields a rectangle when height
  // is the limiting axis, so we measure and set an explicit side in px.
  const cardAreaRef = useRef<HTMLDivElement>(null);
  const [cardSide, setCardSide] = useState(0);
  useEffect(() => {
    const el = cardAreaRef.current;
    if (!el) return;
    const update = () =>
      setCardSide(Math.floor(Math.min(el.clientWidth, el.clientHeight)));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const totalConcepts = carousels.length;
  const currentConcept = carousels[conceptIdx];
  const totalCards = currentConcept?.cards.length ?? 0;
  const currentCard = currentConcept?.cards[cardIdx];

  // All nav wraps around - no dead-ends.
  const goPrevCard = useCallback(() => {
    setCardIdx(i => (i - 1 + totalCards) % totalCards);
  }, [totalCards]);

  const goNextCard = useCallback(() => {
    setCardIdx(i => (i + 1) % totalCards);
  }, [totalCards]);

  const goPrevConcept = useCallback(() => {
    setConceptIdx(i => (i - 1 + totalConcepts) % totalConcepts);
    setCardIdx(0);
  }, [totalConcepts]);

  const goNextConcept = useCallback(() => {
    setConceptIdx(i => (i + 1) % totalConcepts);
    setCardIdx(0);
  }, [totalConcepts]);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNextCard();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrevCard();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        goNextConcept();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        goPrevConcept();
      } else if (e.key === 'Escape' && onClose) {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [goNextCard, goPrevCard, goNextConcept, goPrevConcept, onClose]);

  // Touch swipe - classify by larger delta, with a 50px threshold.
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const handleTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    touchStartRef.current = { x: t.clientX, y: t.clientY };
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    const start = touchStartRef.current;
    if (!start) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    touchStartRef.current = null;

    if (Math.max(Math.abs(dx), Math.abs(dy)) < SWIPE_THRESHOLD) return;

    if (Math.abs(dx) > Math.abs(dy)) {
      // Horizontal swipe → cards
      if (dx < 0) goNextCard();
      else goPrevCard();
    } else {
      // Vertical swipe → concepts
      if (dy < 0) goNextConcept();
      else goPrevConcept();
    }
  };

  if (!currentConcept || !currentCard) {
    return <HighlightsEmptyState />;
  }

  return (
    <div className='group/reader relative flex h-full flex-col overflow-hidden p-4'>
      {/* Concept name + close (card position shown as bottom dots) */}
      <div className='mb-4 flex items-center justify-between gap-3'>
        <ReaderTitle concept={currentConcept} />
        {onClose && (
          <Button
            variant='ghost'
            size='icon'
            onClick={onClose}
            aria-label='Close highlights reader'
            className='tap-target relative z-20 -mr-1 h-7 w-7 flex-shrink-0'
          >
            <X className='h-4 w-4' />
          </Button>
        )}
      </div>

      {/* Card body: lay every card out on a fixed CARD_DESIGN_PX square, then
          uniformly scale it down (like an image) to the largest square that fits
          the available area - so layout/spacing stay proportional at any panel
          width. Swipe to navigate; from md: up the card arrows sit either side. */}
      <div className='flex min-h-0 flex-1 items-center gap-3'>
        {totalCards > 1 && <CardArrow direction='prev' onClick={goPrevCard} />}
        <div
          ref={cardAreaRef}
          className='relative flex min-w-0 flex-1 items-center justify-center self-stretch overflow-hidden'
        >
          {cardSide > 0 && (
            <div
              style={{ width: cardSide, height: cardSide }}
              className='relative overflow-hidden rounded-xl'
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              <div
                key={`${conceptIdx}-${cardIdx}`}
                style={{
                  width: CARD_DESIGN_PX,
                  height: CARD_DESIGN_PX,
                  transform: `scale(${cardSide / CARD_DESIGN_PX})`,
                }}
                className='origin-top-left'
              >
                <TemplateCard
                  template={pickTemplateForConcept(currentConcept.concept_id)}
                  card={currentCard}
                  slideIndex={cardIdx}
                  totalSlides={currentConcept.cards.length}
                  conceptName={currentConcept.concept_name}
                />
              </div>
            </div>
          )}
        </div>
        {totalCards > 1 && <CardArrow direction='next' onClick={goNextCard} />}
      </div>

      {/* Pagination dots - card position within the current concept. The
          dots were the ONLY statement of position, and a width change says
          nothing to a screen reader - so the position is also a live status,
          and the dots are marked decorative. */}
      <p className='sr-only' role='status' aria-live='polite'>
        Card {cardIdx + 1} of {totalCards}
      </p>
      <div
        aria-hidden='true'
        className='mt-3 flex flex-shrink-0 items-center justify-center gap-1.5'
      >
        {currentConcept.cards.map((_, i) => (
          <span
            key={i}
            className={cn(
              'h-1.5 rounded-full transition-all duration-300',
              i === cardIdx ? 'w-4 bg-primary' : 'w-1.5 bg-muted-foreground/30'
            )}
          />
        ))}
      </div>
    </div>
  );
}

/** The deck being read: its concept, under the lesson section it is in. */
function ReaderTitle({ concept }: { concept: HighlightCarousel }) {
  return (
    <div className='min-w-0'>
      {concept.section_title && (
        <p className='truncate font-mono text-xs text-muted-foreground'>
          {concept.section_index != null && `${concept.section_index} · `}
          <InlineMarkdown>{concept.section_title}</InlineMarkdown>
        </p>
      )}
      <p className='truncate text-sm font-semibold text-foreground'>
        <InlineMarkdown>{concept.concept_name}</InlineMarkdown>
      </p>
    </div>
  );
}
